import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { User, FileText, Download, Pencil, ShieldCheck, AlertCircle, CheckCircle2, Cake } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { MyProfileDatesDialog } from '../components/MyProfileDatesDialog';

const TYPE_LABEL = {
  offer_letter: 'Offer Letter',
  experience_letter: 'Experience Letter',
  internship_letter: 'Internship Letter',
  payslip: 'Payslip',
};

const TYPE_COLOR = {
  offer_letter: 'bg-blue-50 text-blue-700 border-blue-200',
  experience_letter: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  internship_letter: 'bg-purple-50 text-purple-700 border-purple-200',
  payslip: 'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * "My Profile" — employee-facing private page.
 * - Shows the current user's HR record (live + pending)
 * - Self-edit a defined subset (everything except salary + employee_id)
 * - Lists & downloads documents tied to their account
 * - Backend enforces strict scoping (own only); Super Admin sees the full HR Master at /hr instead.
 */
export default function MyProfile() {
  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [meRes, docsRes] = await Promise.all([
        api.get('/hr/me'),
        api.get('/hr/documents'),
      ]);
      setMe(meRes.data);
      setDocs(docsRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load profile');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openEdit = () => {
    const seed = { ...(me?.hr_details || {}), ...(me?.hr_details_pending || {}) };
    setForm({
      permanent_address: seed.permanent_address || '',
      pan: seed.pan || '',
      uan: seed.uan || '',
      aadhar: seed.aadhar || '',
      bank_account: seed.bank_account || '',
      ifsc: seed.ifsc || '',
      emergency_contact_name: seed.emergency_contact_name || '',
      emergency_contact_number: seed.emergency_contact_number || '',
      marital_status: seed.marital_status || '',
      blood_group: seed.blood_group || '',
      designation: seed.designation || '',
      department: seed.department || '',
      location: seed.location || '',
    });
    setEditing(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const body = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null) body[k] = v; });
      await api.put('/hr/me', body);
      toast.success('Submitted for Super Admin approval');
      setEditing(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/hr/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${doc.title || doc.doc_type}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (!me) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;
  const h = me.hr_details || {};
  const p = me.hr_details_pending || {};
  const hasPending = Object.keys(p).length > 0;
  const status = me.hr_pending_status;

  return (
    <div className="p-6 min-h-full bg-slate-50" data-testid="my-profile-page">
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <User className="h-6 w-6 text-[#5B6FD6]" /> My Profile
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Your personal & employment details. Changes are sent to Super Admin for approval. Only you and Super Admin can see this.
          </p>
        </div>
        <Button onClick={openEdit} className="bg-[#5B6FD6] hover:bg-[#4338CA] gap-1.5" data-testid="my-profile-edit">
          <Pencil className="h-4 w-4" /> Edit my details
        </Button>
        <Button onClick={() => setDatesOpen(true)} variant="outline" className="gap-1.5" data-testid="my-profile-dates">
          <Cake className="h-4 w-4" /> Set DOB / Joining date
        </Button>
      </div>

      {/* Pending banner */}
      {hasPending && status === 'pending' && (
        <Card className="border-amber-200 bg-amber-50 mb-4" data-testid="pending-banner">
          <CardContent className="p-3 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-0.5">Awaiting Super Admin approval</p>
              <p className="text-xs">
                Fields pending: {Object.keys(p).join(', ')}. They'll go live once approved.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {status === 'rejected' && (
        <Card className="border-red-200 bg-red-50 mb-4" data-testid="rejected-banner">
          <CardContent className="p-3 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-semibold mb-0.5">Your last submission was rejected</p>
              {me.hr_pending_rejection_reason && (
                <p className="text-xs">Reason: {me.hr_pending_rejection_reason}</p>
              )}
              <p className="text-xs mt-1">Please edit and resubmit.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Identity card */}
      <Card className="mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-4 mb-4 pb-4 border-b">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] flex items-center justify-center text-white text-lg font-bold">
              {me.full_name?.charAt(0)}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">{me.full_name}</h2>
              <p className="text-sm text-slate-500">{me.mobile_number} · <span className="capitalize">{me.role?.replace('_', ' ')}</span></p>
            </div>
            <Badge className="bg-slate-100 text-slate-700">
              <ShieldCheck className="h-3 w-3 mr-1" /> Private to you & Super Admin
            </Badge>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <Cell label="Employee ID" value={h.employee_id} />
            <Cell label="Designation" value={h.designation} pending={p.designation} />
            <Cell label="Department" value={h.department} pending={p.department} />
            <Cell label="Location" value={h.location} pending={p.location} />
            <Cell label="Date of birth" value={me.date_of_birth} />
            <Cell label="Joining date" value={me.joining_date} />
            <Cell label="Marital status" value={h.marital_status} pending={p.marital_status} />
            <Cell label="Blood group" value={h.blood_group} pending={p.blood_group} />
            <Cell label="PAN" value={h.pan} pending={p.pan} />
            <Cell label="Aadhar" value={h.aadhar} pending={p.aadhar} />
            <Cell label="UAN" value={h.uan} pending={p.uan} />
            <Cell label="Bank A/c" value={h.bank_account} pending={p.bank_account} />
            <Cell label="IFSC" value={h.ifsc} pending={p.ifsc} />
            <Cell label="Emergency contact" value={h.emergency_contact_name ? `${h.emergency_contact_name} — ${h.emergency_contact_number || ''}` : null}
                  pending={p.emergency_contact_name ? `${p.emergency_contact_name} — ${p.emergency_contact_number || ''}` : null} />
            <div className="col-span-2 lg:col-span-3">
              <Cell label="Permanent address" value={h.permanent_address} pending={p.permanent_address} long />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-[#5B6FD6]" /> My documents
            <span className="text-xs font-normal text-slate-400">({docs.length})</span>
          </h3>
          {docs.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">
              No documents yet. Your HR will push them here when generated.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {docs.map(d => (
                <div key={d.id} className="border rounded-lg p-3" data-testid={`my-doc-${d.id}`}>
                  <div className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border mb-2 ${TYPE_COLOR[d.doc_type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {TYPE_LABEL[d.doc_type] || d.doc_type}
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mb-0.5 line-clamp-1">{d.title}</p>
                  <p className="text-xs text-slate-500 mb-2">{new Date(d.created_at).toLocaleString()}</p>
                  <Button size="sm" onClick={() => download(d)} className="w-full bg-[#5B6FD6] hover:bg-[#4338CA]" data-testid={`download-doc-${d.id}`}>
                    <Download className="h-4 w-4 mr-1.5" /> Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="my-profile-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit my personal details</DialogTitle>
            <DialogDescription>
              Changes go to Super Admin for approval. Salary and Employee ID can only be set by Super Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Designation" value={form.designation} set={(v) => setForm({ ...form, designation: v })} />
            <F label="Department" value={form.department} set={(v) => setForm({ ...form, department: v })} />
            <F label="Work Location" value={form.location} set={(v) => setForm({ ...form, location: v })} />
            <F label="PAN" value={form.pan} set={(v) => setForm({ ...form, pan: v?.toUpperCase() })} />
            <F label="Aadhar" value={form.aadhar} set={(v) => setForm({ ...form, aadhar: v })} />
            <F label="UAN" value={form.uan} set={(v) => setForm({ ...form, uan: v })} />
            <F label="Bank A/c" value={form.bank_account} set={(v) => setForm({ ...form, bank_account: v })} />
            <F label="IFSC" value={form.ifsc} set={(v) => setForm({ ...form, ifsc: v?.toUpperCase() })} />
            <F label="Emergency contact name" value={form.emergency_contact_name} set={(v) => setForm({ ...form, emergency_contact_name: v })} />
            <F label="Emergency contact number" value={form.emergency_contact_number} set={(v) => setForm({ ...form, emergency_contact_number: v })} />
            <div>
              <Label className="text-xs">Marital status</Label>
              <Select value={form.marital_status || 'unset'} onValueChange={(v) => setForm({ ...form, marital_status: v === 'unset' ? '' : v })}>
                <SelectTrigger data-testid="my-marital"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                  <SelectItem value="divorced">Divorced</SelectItem>
                  <SelectItem value="widowed">Widowed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <F label="Blood group" value={form.blood_group} set={(v) => setForm({ ...form, blood_group: v })} />
            <div className="col-span-2">
              <F label="Permanent address" value={form.permanent_address} set={(v) => setForm({ ...form, permanent_address: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#5B6FD6] hover:bg-[#4338CA]" data-testid="my-profile-submit">
              {saving ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MyProfileDatesDialog
        open={datesOpen}
        onOpenChange={setDatesOpen}
        initialDOB={me.date_of_birth || ''}
        initialJoin={me.joining_date || ''}
        onSaved={fetchAll}
      />
    </div>
  );
}

const Cell = ({ label, value, pending, long }) => (
  <div className={long ? '' : ''}>
    <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
    {value
      ? <p className="text-sm text-slate-800">{value}</p>
      : <p className="text-sm text-slate-300">—</p>}
    {pending && pending !== value && (
      <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
        <CheckCircle2 className="h-3 w-3" /> Pending: <span className="font-medium">{pending}</span>
      </p>
    )}
  </div>
);

const F = ({ label, value, set }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input value={value || ''} onChange={(e) => set(e.target.value)} />
  </div>
);
